import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment';

const SCENE_BG = '#12122a';
const MODEL_COLOR = '#d4c4a0';

function createScene(width, height, el) {
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(SCENE_BG);

    const aspect = width / height;
    const frustum = 1;
    const camera = new THREE.OrthographicCamera(
        -frustum * aspect, frustum * aspect, frustum, -frustum, 0.1, 10000,
    );

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 0.7;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    el.appendChild(renderer.domElement);

    // PBR environment map
    const pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    pmrem.dispose();

    // Lighting
    scene.add(new THREE.HemisphereLight('#d4e4ff', '#a09070', 0.9));
    const key = new THREE.DirectionalLight('#fff5e6', 1.6);
    key.position.set(3, 4, 6);
    scene.add(key);
    const fill = new THREE.DirectionalLight('#c8d8f0', 0.5);
    fill.position.set(-5, 2, -1);
    scene.add(fill);
    const rim = new THREE.DirectionalLight('#e0ecff', 1.0);
    rim.position.set(0, -2, -6);
    scene.add(rim);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.06;
    controls.enableZoom = true;
    controls.minPolarAngle = -Infinity;
    controls.maxPolarAngle = Infinity;
    const sph = controls._spherical;
    if (sph) sph.makeSafe = () => sph;

    return { scene, camera, renderer, controls };
}

export async function loadGeometry(file) {
    const name = file.name.toLowerCase();
    const buf = await file.arrayBuffer();
    if (name.endsWith('.stl')) return mergeVertices(new STLLoader().parse(buf));
    if (name.endsWith('.ply')) return mergeVertices(new PLYLoader().parse(buf));
    if (name.endsWith('.obj')) {
        const obj = new OBJLoader().parse(new TextDecoder().decode(buf));
        const first = obj.children.find(c => c.isMesh);
        return first?.geometry ?? new THREE.BufferGeometry();
    }
    return new THREE.BufferGeometry();
}

function fitCamera(camera, controls, box, aspect) {
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z) * 0.6;
    camera.left = -maxDim * aspect;
    camera.right = maxDim * aspect;
    camera.top = maxDim;
    camera.bottom = -maxDim;
    camera.updateProjectionMatrix();
    camera.position.set(0, maxDim * 0.5, maxDim * 2.5);
    camera.lookAt(0, 0, 0);
    controls.target.set(0, 0, 0);
    controls.update();
}

export function makeMaterial(color) {
    return new THREE.MeshPhysicalMaterial({
        color: new THREE.Color(color),
        roughness: 0.7,
        metalness: 0,
        clearcoat: 0.04,
        side: THREE.DoubleSide,
        envMapIntensity: 1.2,
    });
}

const MiniSTLViewer = ({ file, width = 120, height = 120 }) => {
    const mountRef = useRef(null);

    useEffect(() => {
        const el = mountRef.current;
        if (!el) return;

        const { scene, camera, renderer, controls } = createScene(width, height, el);
        const aspect = width / height;

        loadGeometry(file).then(geometry => {
            geometry.computeVertexNormals();
            const mesh = new THREE.Mesh(geometry, makeMaterial(MODEL_COLOR));
            const box = new THREE.Box3().setFromObject(mesh);
            mesh.position.sub(box.getCenter(new THREE.Vector3()));
            scene.add(mesh);
            fitCamera(camera, controls, box, aspect);
        });

        let animId;
        const animate = () => {
            animId = requestAnimationFrame(animate);
            controls.update();
            renderer.render(scene, camera);
        };
        animate();

        return () => {
            cancelAnimationFrame(animId);
            controls.dispose();
            renderer.dispose();
            if (el.contains(renderer.domElement)) el.removeChild(renderer.domElement);
        };
    }, [file, width, height]);

    return <div ref={mountRef} style={{ width, height, borderRadius: 2, overflow: 'hidden' }} />;
};

export default MiniSTLViewer;

/* ── Combined viewer (two meshes in one scene) ── */
export const CombinedMiniSTLViewer = ({ upper, lower, width = 120, height = 180 }) => {
    const mountRef = useRef(null);

    useEffect(() => {
        const el = mountRef.current;
        if (!el || (!upper && !lower)) return;

        const { scene, camera, renderer, controls } = createScene(width, height, el);
        const aspect = width / height;
        const group = new THREE.Group();
        scene.add(group);

        const loadModels = async () => {
            if (upper) {
                const geo = await loadGeometry(upper);
                geo.computeVertexNormals();
                group.add(new THREE.Mesh(geo, makeMaterial('#d4c4a0')));
            }
            if (lower) {
                const geo = await loadGeometry(lower);
                geo.computeVertexNormals();
                group.add(new THREE.Mesh(geo, makeMaterial('#b0a090')));
            }

            const box = new THREE.Box3().setFromObject(group);
            group.position.sub(box.getCenter(new THREE.Vector3()));
            fitCamera(camera, controls, box, aspect);
        };

        loadModels();

        let animId;
        const animate = () => {
            animId = requestAnimationFrame(animate);
            controls.update();
            renderer.render(scene, camera);
        };
        animate();

        return () => {
            cancelAnimationFrame(animId);
            controls.dispose();
            renderer.dispose();
            if (el.contains(renderer.domElement)) el.removeChild(renderer.domElement);
        };
    }, [upper, lower, width, height]);

    return <div ref={mountRef} style={{ width, height, borderRadius: 2, overflow: 'hidden' }} />;
};
